import { useState, useMemo } from 'react';
import { Card, CardContent } from '@components/ui/Card';
import Input from '@components/ui/Input';
import Button from '@components/ui/Button';
import Badge from '@components/ui/Badge';
import Textarea from '@components/ui/Textarea';
import { 
  Search, 
  Plus, 
  Edit2, 
  Trash2, 
  X, 
  Calendar,
  Clock,
  MapPin,
  Check,
  ChevronLeft
} from 'lucide-react';
import { upcomingEvents } from '@data/siteData';
import type { Event } from '../../types';

// Mock initial events data
const initialEvents: Event[] = [...upcomingEvents];

interface EventFormData {
  type: string;
  date: string;
  time: string;
  title: string;
  description: string;
  location: string;
  organizer: string;
  admission: string;
  guide: string;
  maxParticipants: string;
  status: 'available' | 'booked' | 'cancelled';
}

const emptyFormData: EventFormData = {
  type: '',
  date: '',
  time: '',
  title: '',
  description: '',
  location: '',
  organizer: '',
  admission: '',
  guide: '',
  maxParticipants: '',
  status: 'available',
};

function EventForm({ 
  event, 
  onSave, 
  onCancel 
}: { 
  event?: Event; 
  onSave: (data: EventFormData) => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState<EventFormData>(
    event ? {
      type: event.type || '',
      date: event.date || '',
      time: event.time || '',
      title: event.title || '',
      description: event.description || '',
      location: event.location || '',
      organizer: event.organizer || '',
      admission: event.admission || '',
      guide: event.guide || '',
      maxParticipants: event.maxParticipants?.toString() || '',
      status: event.status || 'available',
    } : emptyFormData
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <Button 
          type="button" 
          variant="ghost" 
          onClick={onCancel}
          className="gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          Zurück
        </Button>
        <h2 className="text-xl font-bold text-white">
          {event ? 'Veranstaltung bearbeiten' : 'Neue Veranstaltung'}
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Titel *"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="z.B. Offene Sternwarte"
          required
        />

        <Input
          label="Veranstaltungstyp *"
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value })}
          placeholder="z.B. BESICHTIGUNG, FÜHRUNG"
          required
        />

        <Input
          label="Datum *"
          type="date"
          value={formData.date}
          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
          required
        />

        <Input
          label="Uhrzeit *"
          value={formData.time}
          onChange={(e) => setFormData({ ...formData, time: e.target.value })}
          placeholder="z.B. 11 – 13 Uhr"
          required
        />

        <Input
          label="Ort"
          value={formData.location}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          placeholder="z.B. Alte Sternwarte Mannheim"
        />

        <Input
          label="Veranstalter"
          value={formData.organizer}
          onChange={(e) => setFormData({ ...formData, organizer: e.target.value })}
          placeholder="z.B. Aktionsbündnis Alte Sternwarte"
        />

        <Input
          label="Eintritt"
          value={formData.admission}
          onChange={(e) => setFormData({ ...formData, admission: e.target.value })}
          placeholder="z.B. Eintritt frei, um eine Spende wird gebeten"
        />

        <Input
          label="Guide / Referent"
          value={formData.guide}
          onChange={(e) => setFormData({ ...formData, guide: e.target.value })}
          placeholder="Name des Guides"
        />

        <Input
          label="Max. Teilnehmer"
          type="number"
          value={formData.maxParticipants}
          onChange={(e) => setFormData({ ...formData, maxParticipants: e.target.value })}
          placeholder="z.B. 15"
        />

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1.5">
            Status
          </label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value as 'available' | 'booked' | 'cancelled' })}
            className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-lg text-white
              focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
              transition-all duration-200"
          >
            <option value="available">Verfügbar</option>
            <option value="booked">Ausgebucht</option>
            <option value="cancelled">Abgesagt</option>
          </select>
        </div>
      </div>

      <Textarea
        label="Beschreibung *"
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        placeholder="Beschreiben Sie die Veranstaltung..."
        rows={6}
        required
      />

      <div className="flex items-center justify-end gap-4 pt-4">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Abbrechen
        </Button>
        <Button type="submit" size="lg">
          <Check className="w-4 h-4 mr-2" />
          {event ? 'Speichern' : 'Erstellen'}
        </Button>
      </div>
    </form>
  );
}

export default function EventsManage() {
  const [events, setEvents] = useState<Event[]>(initialEvents);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const filteredEvents = useMemo(() => {
    return events.filter(event => 
      event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.type.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [events, searchQuery]);

  const handleSave = (formData: EventFormData) => {
    if (editingEvent) {
      // Update existing
      setEvents(events.map(e => 
        e.id === editingEvent.id 
          ? { 
              ...e, 
              ...formData,
              maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : undefined,
            }
          : e
      ));
      setEditingEvent(null);
    } else {
      // Create new
      const newEvent: Event = {
        id: Date.now().toString(),
        ...formData,
        maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : undefined,
      };
      setEvents([newEvent, ...events]);
      setIsCreating(false);
    }
  };

  const handleDelete = (id: string) => {
    setEvents(events.filter(e => e.id !== id));
    setDeleteConfirm(null);
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'available':
        return <Badge variant="success">Verfügbar</Badge>;
      case 'booked':
        return <Badge variant="warning">Ausgebucht</Badge>;
      case 'cancelled':
        return <Badge variant="error">Abgesagt</Badge>;
      default:
        return <Badge>Verfügbar</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (isCreating) {
    return (
      <Card>
        <CardContent className="p-6">
          <EventForm onSave={handleSave} onCancel={() => setIsCreating(false)} />
        </CardContent>
      </Card>
    );
  }

  if (editingEvent) {
    return (
      <Card>
        <CardContent className="p-6">
          <EventForm 
            event={editingEvent} 
            onSave={handleSave} 
            onCancel={() => setEditingEvent(null)} 
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Veranstaltungen</h1>
          <p className="text-slate-400 mt-1">
            Verwalten Sie Termine und Events der Sternwarte
          </p>
        </div>
        <Button onClick={() => setIsCreating(true)} size="lg">
          <Plus className="w-5 h-5 mr-2" />
          Neue Veranstaltung
        </Button>
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <Input
            placeholder="Veranstaltungen suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12"
          />
        </div>
      </div>

      {/* Events List */}
      <Card>
        <CardContent className="p-0">
          {filteredEvents.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">
                Keine Veranstaltungen gefunden
              </h3>
              <p className="text-slate-400">
                {searchQuery ? 'Versuchen Sie eine andere Suche.' : 'Erstellen Sie Ihre erste Veranstaltung.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {filteredEvents.map((event) => (
                <div 
                  key={event.id} 
                  className="px-6 py-4 flex items-center justify-between hover:bg-slate-900/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-white font-medium truncate">{event.title}</h3>
                      {getStatusBadge(event.status)}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(event.date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {event.time}
                      </span>
                      {event.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {event.location}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingEvent(event)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    {deleteConfirm === event.id ? (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm(null)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(event.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteConfirm(event.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
